import { useContext, useEffect, useState, useRef } from "react";
import { Box, Menu, MenuItem, ListItemText } from "@mui/material";
import {
  collection,
  onSnapshot,
  collectionGroup,
  doc,
  query,
  where,
  updateDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import MetricCard from "./MetricCard";
import { statusColors } from "../constants/statusColors";
import { useNavigate } from "react-router-dom";
import MobileHeader from "./headers/MobileHearder";
import { ThemeProvider, createTheme } from "@mui/material/styles";
export default function DashboardMobile() {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();

  const [, setRole] = useState("Normal User");
  const [profile, setProfile] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const navigate = useNavigate();

  // 🔦 Dark mode
  const [darkMode, setDarkMode] = useState(false);
  const lightTheme = createTheme({
    palette: {
      mode: "light",
      background: { default: "#f4f8f9", paper: "#fff" },
    },
  });
  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      background: { default: "#121212", paper: "#1e1e1e" },
      text: { primary: "#fff", secondary: "#ccc" },
    },
  });

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role);
      setProfile({ id: snap.id, ...data });
      if (data.darkMode !== undefined) setDarkMode(data.darkMode);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!profile?.companyId) return;
    getDocs(
      query(
        collectionGroup(db, "tickets"),
        where("companyId", "==", profile.companyId)
      )
    ).then((snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const counts = {};
      all.forEach((t) => (counts[t.status] = (counts[t.status] || 0) + 1));
      setStatusCounts(counts);
    });
  }, [profile]);

  useEffect(() => {
    if (!currentUser) return;
    const notifCol = collection(
      db,
      "profilsUsers",
      currentUser.uid,
      "notifications"
    );
    const unsub = onSnapshot(notifCol, (snap) =>
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [currentUser]);

  const handleBellClose = () => setAnchorNotif(null);

  const handleProfileClose = () => setAnchorProfile(null);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        {/* Supprime tout wrapper blanc autour du header */}
        <MobileHeader title={translate("SupportTickets")} />

        <Menu
          anchorEl={anchorNotif}
          open={Boolean(anchorNotif)}
          onClose={handleBellClose}
          PaperProps={{ style: { maxHeight: "50vh", width: 280 } }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary={translate("NoNotifications")} />
            </MenuItem>
          ) : (
            notifications.map((n) => (
              <MenuItem
                key={n.id}
                onClick={async () => {
                  await updateDoc(
                    doc(
                      db,
                      "profilsUsers",
                      currentUser.uid,
                      "notifications",
                      n.id
                    ),
                    {
                      read: true,
                    }
                  );
                  handleBellClose();
                }}
              >
                <ListItemText
                  primary={n.message}
                  secondary={
                    n.timestamp
                      ? new Date(n.timestamp.seconds * 1000).toLocaleString()
                      : ""
                  }
                />
              </MenuItem>
            ))
          )}
        </Menu>

        <Menu
          anchorEl={anchorProfile}
          open={Boolean(anchorProfile)}
          onClose={handleProfileClose}
        >
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/userprofile");
            }}
          >
            <ListItemText primary={translate("Profile")} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/gotopremium");
            }}
          >
            <ListItemText primary={translate("GoPremium")} />
          </MenuItem>
          <MenuItem
            onClick={async () => {
              handleProfileClose();
              await logOut();
              navigate("/login");
            }}
          >
            <ListItemText primary={translate("Logout")} />
          </MenuItem>
        </Menu>

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            pt: "40px",
          }}
        >
          {Object.entries(statusColors).map(([status, color]) => (
            <Box key={status} sx={{ flex: "0 0 45%", mx: "auto" }}>
              <MetricCard
                title={translate(status)}
                value={statusCounts[status] || 0}
                unit=""
                color={color}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
